import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showUpgradeRequired } from './UpgradeRequiredDialog';

const ROLE_HOME = {
  security_officer: '/security',
  driver: '/security/driver',
  parent: '/parent-portal',
  student: '/student-portal',
  teacher: '/teacher-portal',
  super_admin: '/super-admin-portal',
  hr: '/hr-portal',
  nurse: '/nurse-portal',
  registrar: '/registrar-portal',
  storekeeper: '/storekeeper-portal',
  canteen_staff: '/canteen-admin',
};

function ProtectedRoute({ children, roles, permission, module }) {
  const { user, loading, can, hasModule } = useAuth();
  const location = useLocation();

  // Plan-tier gate (services/plan_gating.py) — additive to `roles`/
  // `permission` above. Catches direct navigation to a gated route's URL,
  // which the nav sidebar's own lock-icon click-interception (Layout.js)
  // can't — the sidebar only stops a click, not a typed/bookmarked URL.
  // showUpgradeRequired() has to live in an effect, not inline in the
  // render below, since firing it during render would violate React's
  // no-side-effects-in-render rule.
  const moduleLocked = Boolean(module) && Boolean(user) && !hasModule(module);
  useEffect(() => {
    if (moduleLocked) {
      showUpgradeRequired();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleLocked]);

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Fine-grained gate, additive to `roles` below — both may be given
  // together (e.g. roles as a coarse portal check, permission for a
  // specific migrated action within it). See AuthContext.js's `can()`.
  if (permission && !can(permission)) {
    return <Navigate to={ROLE_HOME[user.role] || '/dashboard'} />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to={ROLE_HOME[user.role] || '/dashboard'} />;
  }

  if (moduleLocked) {
    return <Navigate to={ROLE_HOME[user.role] || '/dashboard'} />;
  }

  return children;
}

export default ProtectedRoute;
